import { useGetMemberMutation, usePostMembersMutation, usePutMembersMutation } from '@services/member/member/member.endpoints';
import { MemberToCreate } from '@services/member/member/member.types';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export const useAddPageVM = () => {
    const navigate = useNavigate();

    const { id } = useParams();
    const [member, setMember] = useState<MemberToCreate>();
    // Tracks which member id the currently loaded form data belongs to.
    // Used to prevent saving one member's data onto another member's record
    // when navigating between edit pages before the new data has loaded.
    const [loadedMemberId, setLoadedMemberId] = useState<string>();

    const [
        getMember,
        {
            isLoading: isGetMemberLoading,
            isError: isGetMemberError,
            error: getMemberError,
            isSuccess: isGetMemberSuccess,
            data: getMemberResponse
        }
    ] = useGetMemberMutation();

    const [
        createMember,
        {
            isLoading: isCreateMemberLoading,
            isError: isCreateMemberError,
            isSuccess: isCreateMemberSuccess,
            error: createMemberError,
            data: createMemberResponse
        }
    ] = usePostMembersMutation();

    const [
        updateMember,
        {
            isLoading: isUpdateMemberLoading,
            isError: isUpdateMemberError,
            error: updateMemberError,
            isSuccess: isUpdateMemberSuccess,
            data: updateMemberResponse
        }
    ] = usePutMembersMutation();

    useEffect(() => {
        if (!id) return;
        // Clear any previously loaded member so the form never shows/submits
        // stale data belonging to a different member while the new one loads.
        setMember(undefined);
        setLoadedMemberId(undefined);
        getMember({ id });
    }, [id]);


    useEffect(() => {
        if (isUpdateMemberLoading) return;
        if (isUpdateMemberError) return;
        if (updateMemberError) return;

        if (isUpdateMemberSuccess && updateMemberResponse) {
            navigate(`/members-management/members/${updateMemberResponse.data.id}/details`);
        }
    }, [isUpdateMemberError, isUpdateMemberSuccess, updateMemberError, updateMemberResponse, isUpdateMemberLoading, navigate]);

    useEffect(() => {
        if (isCreateMemberLoading) return;
        if (isCreateMemberError) return;
        if (createMemberError) return;

        if (isCreateMemberSuccess && createMemberResponse) {
            navigate(`/members-management/members/${createMemberResponse.data.id}/details`);
        }
    }, [isCreateMemberError, isCreateMemberSuccess, createMemberError, createMemberResponse, isCreateMemberLoading, navigate]);

    useEffect(() => {
        if (isGetMemberLoading) return;
        if (isGetMemberError) return;
        if (getMemberError) return;

        if (isGetMemberSuccess && getMemberResponse) {
            const memberUpdated = { ...getMemberResponse.data, contactInformation: { ...getMemberResponse.data.contactInformation, isEmailExisting: getMemberResponse.data.contactInformation.isEmailExisting || true } }
            setMember(memberUpdated);
            setLoadedMemberId(String(getMemberResponse.data.id));
        }
    }, [isGetMemberError, isGetMemberSuccess, getMemberError, getMemberResponse, isGetMemberLoading]);


    const submitForm = async (values: MemberToCreate) => {
        if (id) {
            // Guard against cross-record overwrites: only update when the loaded
            // form data actually belongs to the member id in the URL. If they
            // differ (e.g. navigation race, stale form), abort instead of
            // saving one member's data onto another.
            if (!member || loadedMemberId !== String(id)) {
                return;
            }
            // Joint l'id du membre chargé au corps : le backend rejette (400) si
            // ce payload ne correspond pas au membre visé.
            await updateMember({ id: id, member: { ...values, id: Number(loadedMemberId) } });
        } else {
            await createMember(values);
        }
    }

    return {
        member,
        isEdit: !!id,
        submitForm,
        isLoading: isCreateMemberLoading || isGetMemberLoading || isUpdateMemberLoading
    };
};
